import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import ToastContainer from './components/Toast';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { useDispatch } from 'react-redux';
import { getCurrentUser } from './store/slices/authSlice';
import authService from './services/authService';
import './i18n';

// Import pages
import Dashboard from './pages/Dashboard';
import Cases from './pages/Cases';
import CaseDetails from './pages/CaseDetails';
import Clients from './pages/Clients';
import Documents from './pages/Documents';
import Calendar from './pages/Calendar';
import Settings from './pages/Settings';
import OrganizationSettings from './pages/OrganizationSettings';
import Reports from './pages/Reports';
import Proforma from './pages/billing/Proforma';
import Bills from './pages/billing/Bills';
import Revenues from './pages/billing/Revenues';
import Users from './pages/Users';
import Contacts from './pages/Contacts';
import Chat from './pages/Chat';
import Tasks from './pages/Tasks';
import CustomersReview from './pages/CustomersReview';
import Reviews from './pages/Reviews';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import PublicCaseDetails from './pages/PublicCaseDetails';
import ProtectedRoute from './components/ProtectedRoute';
import Firms from './pages/Firms';
import AuditLog from './pages/AuditLog';
import UpdatePrompt from './components/UpdatePrompt';

function App() {
  const dispatch = useDispatch();

  // Initialize authentication on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token'); // This checks if token is valid (not expired)
      
      if (token) {
        // Token exists - restore user session from localStorage
        try {
          await dispatch(getCurrentUser() as any);
          console.log('User session restored on app startup');
        } catch (error) {
          console.error('Failed to restore user session:', error);
        }
      }
    };

    initializeAuth();
    
    // Setup axios interceptor for automatic token refresh
    authService.setupAxiosInterceptor();
  }, [dispatch]);

  return (
    <ConfirmDialogProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/public/case/:caseId/:firmId/:code" element={<PublicCaseDetails />} />
          
          {/* Protected Routes */}
          <Route path="/*" element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  {/* Dashboard is role-aware on its own (renders a
                      different overview per role, including super-admin). */}
                  <Route path="/" element={<Dashboard />} />

                  {/* Case-workflow routes: firm-level tenant work, not a
                      Super Admin concern (Phase 8) — excluded from
                      super-admin, open to everyone else. */}
                  <Route path="/cases" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Cases />
                    </ProtectedRoute>
                  } />
                  <Route path="/cases/:id" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <CaseDetails />
                    </ProtectedRoute>
                  } />
                  <Route path="/clients" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Clients />
                    </ProtectedRoute>
                  } />
                  <Route path="/documents" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Documents />
                    </ProtectedRoute>
                  } />
                  <Route path="/calendar" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Calendar />
                    </ProtectedRoute>
                  } />
                  <Route path="/billing/proforma" element={
                    <ProtectedRoute requiredRole="admin">
                      <Proforma />
                    </ProtectedRoute>
                  } />
                  <Route path="/billing/bills" element={
                    <ProtectedRoute requiredRole="admin">
                      <Bills />
                    </ProtectedRoute>
                  } />
                  <Route path="/billing/revenues" element={
                    <ProtectedRoute requiredRole="admin">
                      <Revenues />
                    </ProtectedRoute>
                  } />
                  <Route path="/contacts" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Contacts />
                    </ProtectedRoute>
                  } />
                  <Route path="/chat" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Chat />
                    </ProtectedRoute>
                  } />
                  <Route path="/tasks" element={
                    <ProtectedRoute requiredRole={["admin", "lawyer", "staff", "client"]}>
                      <Tasks />
                    </ProtectedRoute>
                  } />
                  <Route path="/customers-review" element={
                    <ProtectedRoute requiredRole="admin">
                      <Reviews />
                    </ProtectedRoute>
                  } />
                  <Route path="/settings" element={<Settings />} />
                  
                  {/* Super Admin Only Routes */}
                  <Route path="/firms" element={
                    <ProtectedRoute requiredRole="super-admin">
                      <Firms />
                    </ProtectedRoute>
                  } />
                  <Route path="/audit-log" element={
                    <ProtectedRoute requiredRole="super-admin">
                      <AuditLog />
                    </ProtectedRoute>
                  } />
                  <Route path="/users" element={
                    <ProtectedRoute requiredRole={["super-admin", "admin"]}>
                      <Users />
                    </ProtectedRoute>
                  } />
                  <Route path="/reports" element={
                    <ProtectedRoute requiredRole={["super-admin", "admin"]}>
                      <Reports />
                    </ProtectedRoute>
                  } />
                  <Route path="/settings/organization" element={
                    <ProtectedRoute requiredRole="admin">
                      <OrganizationSettings />
                    </ProtectedRoute>
                  } />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
      <UpdatePrompt />
      <ToastContainer />
    </ConfirmDialogProvider>
  );
}

export default App;
